import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw, Check, ArrowRight, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { speakText } from './speakText';

const W = 640;
const H = 280;
const fontFor = (text) => `bold ${Math.min(200, Math.floor((W * 0.85) / Math.max(1, text.length * 0.62)))}px "Fredoka", "Comic Sans MS", sans-serif`;

const drawText = (ctx, text, mode) => {
  ctx.clearRect(0, 0, W, H);
  ctx.font = fontFor(text);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (mode === 'guide') {
    ctx.setLineDash([10, 8]);
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#94a3b8';
    ctx.strokeText(text, W / 2, H / 2 + 8);
    ctx.fillStyle = 'rgba(148,163,184,0.15)';
    ctx.fillText(text, W / 2, H / 2 + 8);
  } else {
    ctx.fillStyle = '#000';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = mode === 'loose' ? 20 : 1;
    ctx.fillText(text, W / 2, H / 2 + 8);
    if (mode === 'loose') ctx.strokeText(text, W / 2, H / 2 + 8);
  }
};

const maskOf = (text, mode) => {
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  drawText(ctx, text, mode);
  return ctx.getImageData(0, 0, W, H).data;
};

// coverage: share of the letter shape the child went over; accuracy: share of ink that stayed on the letter
export const scoreTrace = (inkData, coreData, looseData) => {
  let core = 0, coreHit = 0, ink = 0, inkHit = 0;
  for (let i = 3; i < inkData.length; i += 16) {
    const inked = inkData[i] > 0;
    if (coreData[i] > 0) { core += 1; if (inked) coreHit += 1; }
    if (inked) { ink += 1; if (looseData[i] > 0) inkHit += 1; }
  }
  return { coverage: core ? coreHit / core : 0, accuracy: ink ? inkHit / ink : 0, inkRatio: core ? ink / core : 0 };
};

export const tracePassed = ({ coverage, accuracy, inkRatio }) => coverage >= 0.5 && accuracy >= 0.75 && inkRatio <= 2.5;

export const TraceWordStep = ({ words, onDone }) => {
  const [idx, setIdx] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [tries, setTries] = useState(0);
  const guideRef = useRef(null);
  const inkRef = useRef(null);
  const drawing = useRef(false);
  const word = words[idx];

  const reset = useCallback(() => {
    inkRef.current?.getContext('2d').clearRect(0, 0, W, H);
    setFeedback(null);
  }, []);

  useEffect(() => {
    if (!word) return;
    drawText(guideRef.current.getContext('2d'), word, 'guide');
    reset(); setTries(0);
    speakText(`Trace ${word}`);
  }, [word, reset]);

  const point = (e) => {
    const r = inkRef.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H];
  };
  const down = (e) => {
    drawing.current = true;
    const ctx = inkRef.current.getContext('2d');
    ctx.lineWidth = 22; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#0d9488';
    ctx.beginPath(); ctx.moveTo(...point(e)); ctx.lineTo(...point(e)); ctx.stroke();
    inkRef.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = inkRef.current.getContext('2d');
    ctx.lineTo(...point(e)); ctx.stroke();
  };
  const up = () => { drawing.current = false; };

  const check = () => {
    const ink = inkRef.current.getContext('2d').getImageData(0, 0, W, H).data;
    const s = scoreTrace(ink, maskOf(word, 'core'), maskOf(word, 'loose'));
    const ok = tracePassed(s);
    const tooShort = s.coverage < 0.5;
    setTries((t) => t + 1);
    setFeedback(ok ? 'great' : tooShort ? 'more' : 'inside');
    speakText(ok ? 'Beautiful tracing!' : tooShort ? 'Keep going, trace the whole thing.' : 'Try to stay on the dotted lines.');
  };

  const next = () => (idx + 1 >= words.length ? onDone() : setIdx((i) => i + 1));
  if (!word) return null;
  return (
    <div className="rounded-3xl border-4 border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-teal-50 p-5 sm:p-8 text-center" data-testid="trace-word-step">
      <p className="text-sm font-semibold text-indigo-700 mb-3">Trace it with your finger · {idx + 1} / {words.length}</p>
      <div className="relative mx-auto w-full max-w-[640px] aspect-[640/280] rounded-2xl bg-white shadow-inner border border-indigo-100">
        <canvas ref={guideRef} width={W} height={H} className="absolute inset-0 w-full h-full" />
        <canvas ref={inkRef} width={W} height={H} className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
          onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} data-testid="trace-word-canvas" />
      </div>
      <div className="min-h-[32px] mt-3 font-semibold" data-testid="trace-word-feedback">
        {feedback === 'great' && <span className="text-teal-700">Beautiful tracing!</span>}
        {feedback === 'more' && <span className="text-amber-700">Keep going — trace the whole thing.</span>}
        {feedback === 'inside' && <span className="text-amber-700">Try to stay on the dotted lines.</span>}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
        <Button variant="outline" className="rounded-full" onClick={() => speakText(word)} data-testid="trace-word-hear-btn"><Volume2 className="w-4 h-4 mr-2" /> Hear it</Button>
        <Button variant="outline" className="rounded-full" onClick={reset} data-testid="trace-word-clear-btn"><RotateCcw className="w-4 h-4 mr-2" /> Try again</Button>
        {feedback === 'great' || tries >= 2 ? (
          <Button className="rounded-full bg-teal-600 hover:bg-teal-700" onClick={next} data-testid="trace-word-next-btn">Next <ArrowRight className="w-4 h-4 ml-2" /></Button>
        ) : (
          <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={check} data-testid="trace-word-check-btn"><Check className="w-4 h-4 mr-2" /> I did it!</Button>
        )}
      </div>
    </div>
  );
};

export default TraceWordStep;
